import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Play, RotateCcw, Download } from 'lucide-react';

/*
 * Kojiru · The Recourse Manifesto · 45-second typographic film
 *
 * Screen-record this page in one take at 1600×900 (or full-screen), then
 * drop /downloads/kojiru-recourse-voiceover.mp3 on top in CapCut or Descript.
 * Every scene is tuned to Onyx-voice cadence at speed 0.95.
 */

const RUN_MS = 45000;

const SCENES = [
  { id: 1, start: 0,     end: 9000,  kind: 'world-at-scale' },
  { id: 2, start: 9000,  end: 18000, kind: 'imperfect-world' },
  { id: 3, start: 18000, end: 25000, kind: 'the-promise' },
  { id: 4, start: 25000, end: 38000, kind: 'the-mechanic' },
  { id: 5, start: 38000, end: 42000, kind: 'tagline' },
  { id: 6, start: 42000, end: 45000, kind: 'wordmark' },
];

const SUBTITLES = [
  { start: 0,     end: 9000,  text: 'Every day, autonomous AI agents make billions of financial decisions on behalf of real people. Most of the time, they get it right.' },
  { start: 9000,  end: 18000, text: 'But in an imperfect world, neither people nor autonomous agents make every transaction flawless. Commerce always carries the risk of error, misuse, or harm.' },
  { start: 18000, end: 25000, text: 'When an agent harms a consumer, the record shows what was authorized and who stood behind it.' },
  { start: 25000, end: 38000, text: 'Not in forty days. A signed mandate, and a public pledge the operator posted before the agent went to work. Not insurance. Not a promise of repayment.' },
  { start: 38000, end: 42000, text: 'Give the consumers what they want.' },
  { start: 42000, end: 45000, text: 'Kojiru. The recourse layer for agentic commerce.' },
];

// ── Counter that eases from → to over duration ──────────────────────────
const Counter = ({ from, to, duration = 1400, prefix = '', suffix = '' }) => {
  const [n, setN] = useState(from);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (to - from) * e));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span>{prefix}{n.toLocaleString()}{suffix}</span>;
};

// ─── Scene 1 · World at scale ──────────────────────────────────────────
const WorldAtScaleScene = () => (
  <motion.div
    key="s1"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex flex-col items-center justify-center bg-black"
  >
    {/* Animated stat block */}
    <div className="text-center">
      <div className="text-[10px] tracking-[4px] text-emerald-400 mb-6" data-testid="film-scene-1-eyebrow">
        AUTONOMOUS AGENTS · IN PRODUCTION · TODAY
      </div>
      <div className="text-7xl sm:text-8xl md:text-9xl font-black tabular-nums text-white tracking-tight leading-none">
        <Counter from={0} to={500000000} duration={4200} />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 4.3, duration: 0.5 }}
        className="mt-4 text-xl sm:text-2xl text-slate-400"
      >
        agents making billions of decisions
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 5.5, duration: 0.8 }}
        className="mt-8 text-sm tracking-[3px] text-slate-500"
      >
        MOST OF THE TIME · THEY GET IT RIGHT
      </motion.div>
    </div>
  </motion.div>
);

// ─── Scene 2 · Imperfect world ─────────────────────────────────────────
const ImperfectWorldScene = () => {
  const words = ['error', 'misuse', 'harm'];
  return (
    <motion.div
      key="s2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="absolute inset-0 flex flex-col items-center justify-center bg-black"
    >
      <div className="text-center max-w-4xl px-8">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="text-[10px] tracking-[4px] text-rose-400 mb-6"
        >
          BUT THIS IS AN IMPERFECT WORLD
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.1] mb-10"
        >
          Neither people nor agents<br />make every transaction flawless.
        </motion.h2>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.6, duration: 0.6 }}
          className="text-xs tracking-[3px] text-slate-500 mb-4"
        >
          COMMERCE ALWAYS CARRIES THE RISK OF
        </motion.div>
        <div className="flex items-center justify-center gap-6 sm:gap-10">
          {words.map((w, i) => (
            <motion.span
              key={w}
              initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ delay: 4 + i * 0.7, duration: 0.5 }}
              className="text-2xl sm:text-4xl font-black text-rose-400 tracking-tight"
            >
              {w}.
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Scene 3 · The Promise ─────────────────────────────────────────────
const ThePromiseScene = () => (
  <motion.div
    key="s3"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center bg-black"
  >
    <div className="text-center max-w-4xl px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="text-[10px] tracking-[4px] text-emerald-400 mb-8"
      >
        WHAT MAKES THEM WHOLE
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.8 }}
        className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.1] tracking-tight"
      >
        When an agent harms them,<br />
        <span className="text-emerald-400">the record shows who stood behind it.</span>
      </motion.h2>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.5, duration: 0.6 }}
        className="mt-10 flex items-center justify-center gap-4 text-slate-500 text-sm tracking-[2px]"
      >
        <span className="w-12 h-px bg-slate-700" />
        PLEDGED · PUBLIC · ON-CHAIN
        <span className="w-12 h-px bg-slate-700" />
      </motion.div>
    </div>
  </motion.div>
);

// ─── Scene 4 · The Mechanic ────────────────────────────────────────────
const TheMechanicScene = () => (
  <motion.div
    key="s4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center bg-black"
  >
    <div className="w-full max-w-6xl px-8">
      {/* NOT lines */}
      <div className="mb-10 space-y-3">
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex items-baseline gap-4"
        >
          <span className="text-4xl sm:text-5xl font-black text-rose-400 line-through decoration-4">Not</span>
          <span className="text-3xl sm:text-4xl font-black text-slate-300">in forty days.</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.6, duration: 0.5 }}
          className="flex items-baseline gap-4"
        >
          <span className="text-4xl sm:text-5xl font-black text-rose-400 line-through decoration-4">Not</span>
          <span className="text-3xl sm:text-4xl font-black text-slate-300">after a chargeback fight.</span>
        </motion.div>
      </div>

      {/* Emerald resolution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.5, duration: 0.7 }}
        className="border-l-4 border-emerald-500 pl-6"
      >
        <div className="text-[10px] tracking-[4px] text-emerald-400 mb-2">KOJIRU · PUBLIC EVIDENCE</div>
        <div className="text-4xl sm:text-6xl font-black text-white leading-tight">
          On the record.<br />
          <span className="text-emerald-400">In public.</span>
        </div>
      </motion.div>

      {/* Pledge card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 7, duration: 0.6 }}
        className="mt-10 border border-emerald-500/30 bg-emerald-500/[0.04] rounded-xl px-6 py-4 max-w-2xl"
        data-testid="film-pledge-card"
      >
        <div className="text-[10px] tracking-[3px] text-emerald-300 mb-2">PLEDGED · PUBLIC · USDC ON BASE</div>
        <div className="text-2xl sm:text-3xl font-black text-white leading-snug">
          Posted before the agent ever went to work.
        </div>
      </motion.div>
    </div>
  </motion.div>
);

// ─── Scene 5 · Tagline ─────────────────────────────────────────────────
const TaglineScene = () => (
  <motion.div
    key="s5"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    className="absolute inset-0 flex items-center justify-center bg-black"
  >
    <div className="text-center px-8">
      <motion.h1
        initial={{ opacity: 0, letterSpacing: '0.3em' }}
        animate={{ opacity: 1, letterSpacing: '-0.01em' }}
        transition={{ duration: 1.4, ease: 'easeOut' }}
        className="text-5xl sm:text-6xl md:text-8xl font-black text-white leading-[1] tracking-tight"
      >
        Give the consumers<br />
        <span className="text-emerald-400">what they want.</span>
      </motion.h1>
    </div>
  </motion.div>
);

// ─── Scene 6 · Wordmark ────────────────────────────────────────────────
const WordmarkScene = () => (
  <motion.div
    key="s6"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    className="absolute inset-0 flex items-center justify-center bg-black"
  >
    <div className="text-center">
      <motion.div
        initial={{ letterSpacing: '0.5em', opacity: 0 }}
        animate={{ letterSpacing: '0.14em', opacity: 1 }}
        transition={{ duration: 1.4, ease: 'easeOut' }}
        className="text-6xl sm:text-8xl font-black text-white tracking-widest"
      >
        KOJIRU
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="mt-5 text-[11px] tracking-[6px] text-emerald-400"
      >
        THE RECOURSE LAYER FOR AGENTIC COMMERCE
      </motion.div>
    </div>
  </motion.div>
);

const SCENE_MAP = {
  'world-at-scale': WorldAtScaleScene,
  'imperfect-world': ImperfectWorldScene,
  'the-promise': ThePromiseScene,
  'the-mechanic': TheMechanicScene,
  'tagline': TaglineScene,
  'wordmark': WordmarkScene,
};

// ─── Main film component ────────────────────────────────────────────────
export default function FilmRecourse() {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const startedAt = useRef(null);
  const rafRef = useRef(null);
  const audioRef = useRef(null);

  const tick = useCallback((now) => {
    const elapsed = now - startedAt.current;
    if (elapsed >= RUN_MS) {
      setT(RUN_MS);
      setPlaying(false);
      return;
    }
    setT(elapsed);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const play = useCallback(() => {
    startedAt.current = performance.now();
    setT(0);
    setPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [tick]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const activeScene = SCENES.find((s) => t >= s.start && t < s.end);
  const activeSub = SUBTITLES.find((s) => t >= s.start && t < s.end);
  const ActiveComp = activeScene ? SCENE_MAP[activeScene.kind] : null;

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" data-testid="film-recourse">
      <Helmet>
        <title>The Recourse Manifesto · Kojiru</title>
        <meta name="description" content="A 45-second film. When an AI agent harms a consumer, the record shows what was authorized and who stood behind it." />
      </Helmet>

      {/* Preload the voiceover so play() can trigger it immediately */}
      <audio
        ref={audioRef}
        preload="auto"
        src="/downloads/kojiru-recourse-voiceover.mp3"
        data-testid="film-audio-element"
      />

      {/* 16:9 stage */}
      <div className="relative w-screen h-screen bg-black flex items-center justify-center">
        <div className="relative w-full max-w-[1600px] aspect-video bg-gradient-to-b from-[#0a0a0f] to-black border border-white/5 overflow-hidden">

          {/* Pre-roll */}
          {!playing && t === 0 && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="text-[10px] tracking-[4px] text-emerald-400 mb-4">KOJIRU · 45-SECOND FILM</div>
              <h1 className="text-5xl sm:text-6xl font-black mb-3 tracking-tight text-center px-6">
                The Recourse Manifesto
              </h1>
              <p className="text-sm tracking-[2px] text-slate-400 mb-8 text-center px-6 max-w-xl">
                Give the consumers what they want.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={play}
                  data-testid="film-play-button"
                  className="group flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-slate-200 transition-colors"
                >
                  <Play className="w-5 h-5 fill-current" />
                  Play the film
                </button>
                <a
                  href="/downloads/kojiru-recourse-voiceover.mp3"
                  download
                  data-testid="film-download-mp3"
                  className="flex items-center gap-2 px-6 py-4 rounded-full border border-white/20 text-white font-bold hover:bg-white/5 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Voiceover MP3
                </a>
              </div>
              <p className="mt-6 text-xs text-slate-500 max-w-md text-center leading-relaxed px-6">
                Screen-record this page in one take at 1600&thinsp;&times;&thinsp;900. Voiceover triggers with playback. Export from CapCut or Descript for LinkedIn / X.
              </p>
            </div>
          )}

          {/* Scene */}
          <AnimatePresence mode="wait">
            {ActiveComp && <ActiveComp />}
          </AnimatePresence>

          {/* Subtitle */}
          <AnimatePresence>
            {playing && activeSub && (
              <motion.div
                key={activeSub.start}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-4xl px-6 py-3 rounded bg-black/50 backdrop-blur-sm"
                data-testid="film-subtitle"
              >
                <p className="text-lg sm:text-xl text-white text-center leading-snug tracking-[0.01em]">
                  {activeSub.text}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Timeline */}
          {playing && (
            <div className="absolute bottom-0 inset-x-0 h-1 bg-white/5" data-testid="film-timeline">
              <motion.div className="h-full bg-emerald-500" style={{ width: `${(t / RUN_MS) * 100}%` }} />
            </div>
          )}

          {/* Restart */}
          {!playing && t > 0 && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
              <div className="text-[10px] tracking-[4px] text-emerald-400 mb-4">END OF FILM</div>
              <button
                onClick={play}
                data-testid="film-restart-button"
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-bold hover:bg-white/20 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Play again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
